import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

/** Options for invoking edge functions */
interface InvokeOptions {
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
}

/**
 * Invoke an edge function with automatic 401 retry.
 * On 401, refreshes the session and retries once with a fresh token.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T passes through to supabase.functions.invoke<T>
export async function invokeWithRetry<T = unknown>(
  functionName: string,
  options?: InvokeOptions
): Promise<{ data: T | null; error: Error | null }> {
  // First, get a fresh session
  const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession();
  if (sessionError || !sessionData?.session) {
    return { data: null, error: new Error('Not authenticated') };
  }

  const invokeOptions = {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
  };

  const { data, error } = await supabase.functions.invoke<T>(functionName, invokeOptions);

  // If 401, try refreshing session and retry once
  if (error && (error.message?.includes('401') || (error as { status?: number }).status === 401)) {
    const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshedData?.session) {
      return { data: null, error: new Error('Session refresh failed') };
    }

    const retryOptions = {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${refreshedData.session.access_token}`,
      },
    };

    return supabase.functions.invoke<T>(functionName, retryOptions);
  }

  return { data, error };
}
